import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import { Input } from 'components/ui/Form';
import { Flatpickr } from './Flatpickr';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useMergedRef } from 'hooks';
import flatpickrCSS from "flatpickr/dist/themes/light.css?inline";
import {
  injectStyles,
  insertStylesToHead,
  makeStyleTag,
} from "utils/dom/injectStylesToHead";

const styles = `@layer vendor {
  ${flatpickrCSS}
}`;

const sheet = makeStyleTag();
injectStyles(sheet, styles);
insertStylesToHead(sheet);

const convert12hTo24h = (time12h) => {
  if (!time12h || typeof time12h !== 'string') return time12h;

  const match = time12h.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12h;

  let [_, hoursStr, minutesStr, ampm] = match;
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  ampm = ampm.toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `${hh}:${mm}`;
};

const convert24hTo12h = (time24h) => {
  if (!time24h || typeof time24h !== 'string' || !time24h.includes(':')) return time24h;
  const parts = time24h.split(':');
  let hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (isNaN(hour) || isNaN(minute)) return time24h;

  const ampm = hour >= 12 ? 'PM' : 'AM';
  let hour12 = hour % 12;
  if (hour12 === 0) hour12 = 12;
  const mm = String(minute).padStart(2, '0');
  return `${hour12}:${mm} ${ampm}`;
};

const TimePicker = forwardRef(({
  value,
  onChange,
  className,
  min,
  max,
  ...props
}, ref) => {
  const fp = useRef(null);

  useImperativeHandle(ref, () => {
    return {
      focus() {
        fp.current?.flatpickr?.input?.focus();
      },
      blur() {
        fp.current?.flatpickr?.input?.blur();
      },
    };
  }, []);

  const mergedRef = useMergedRef(fp, ref);

  const options = {
    enableTime: true,
    noCalendar: true,
    dateFormat: "h:i K",
    time_24hr: false,
    disableMobile: true,
    minTime: min || undefined,
    maxTime: max || undefined,
    onReady: (selectedDates, dateStr, instance) => {
      const limitLength = (e) => {
        if (e.target.value.length > 2) {
          e.target.value = e.target.value.slice(0, 2);
        }
      };
      if (instance.hourElement) {
        instance.hourElement.addEventListener("input", limitLength);
      }
      if (instance.minuteElement) {
        instance.minuteElement.addEventListener("input", limitLength);
      }
    },
  };

  const handleFlatpickrChange = (selectedDates, dateStr) => {
    if (onChange) {
      const converted = convert12hTo24h(dateStr);
      onChange({
        target: {
          value: converted
        }
      });
    }
  };

  const displayValue = convert24hTo12h(value);

  const handleClockIconClick = (e) => {
    e.stopPropagation();
    fp.current?.flatpickr?.open();
  };

  return (
    <Flatpickr
      options={options}
      value={displayValue}
      onChange={handleFlatpickrChange}
      ref={mergedRef}
      render={({ defaultValue, value: fpValue, ...flatpickrProps }, fpRef) => {
        return (
          <Input
            ref={fpRef}
            className={className}
            type="text"
            labelProps={{
              onClick: (e) => {
                e.preventDefault();
                e.stopPropagation();
              },
              ...props.labelProps
            }}
            suffix={
              <button
                type="button"
                onClick={handleClockIconClick}
                className="focus:outline-none cursor-pointer"
              >
                <ClockIcon className="size-5 text-gray-400" />
              </button>
            }
            {...props}
            {...flatpickrProps}
            value={fpValue}
            defaultValue={defaultValue}
          />
        );
      }}
    />
  );
});

TimePicker.displayName = "TimePicker";

export { TimePicker };


